/**
 * Queries and mutations for the agents workspace (FOR-326). Every key starts
 * with "cody", so a decision or a setting change refreshes the whole
 * workspace with one invalidation.
 */

import axios from "axios"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { agentsApi, type AgentBudget, type AgentStatus, type RepoPatch, type RunListParams, type SecretKey, type SettingsPatch } from "@/lib/agents-api"
import { isLivePhase, openRunsByTask, type ErrorBody } from "@/lib/cody-agents"

export const CODY_KEY = "cody"

/** Status and body of a failed request, for the error helpers in cody-agents.ts. */
export function errorParts(error: unknown): { status: number | undefined; body: ErrorBody } {
  if (axios.isAxiosError(error)) return { status: error.response?.status, body: (error.response?.data ?? null) as ErrorBody }
  return { status: undefined, body: { error: error instanceof Error ? error.message : undefined } }
}

const poll = (ms: number) => ({ refetchInterval: ms, refetchIntervalInBackground: false })

export function useAgentsOverview(enabled = true) {
  return useQuery({ queryKey: [CODY_KEY, "overview"], queryFn: agentsApi.overview, enabled, ...poll(15_000) })
}

/** The Setup page polls every 10 s; the sidebar asks with a slower interval. */
export function useAgentsSetup(enabled = true, intervalMs = 10_000) {
  return useQuery({ queryKey: [CODY_KEY, "setup"], queryFn: agentsApi.setup, enabled, ...poll(intervalMs) })
}

export function useAgentRuns(params: RunListParams, enabled = true) {
  return useQuery({
    queryKey: [CODY_KEY, "runs", params],
    queryFn: () => agentsApi.runs(params),
    enabled,
    placeholderData: keepPreviousData,
    ...poll(15_000),
  })
}

/** One run plus a page of its transcript; follows a live run every 3 s. */
export function useAgentRun(id: number, page = 1) {
  return useQuery({
    queryKey: [CODY_KEY, "run", id, page],
    queryFn: () => agentsApi.run(id, { page }),
    enabled: Number.isFinite(id),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const phase = query.state.data?.data.phase
      if (!phase) return false
      return isLivePhase(phase) || phase === "queued" ? 3000 : 30_000
    },
    refetchIntervalInBackground: false,
  })
}

export function useReviewQueue(enabled = true) {
  return useQuery({ queryKey: [CODY_KEY, "review"], queryFn: () => agentsApi.review({ per_page: 100 }), enabled, ...poll(30_000) })
}

/** How many runs wait for the owner: one row, the paginator's total. */
export function useCodyWaitingCount(enabled = true): number {
  const { data } = useQuery({ queryKey: [CODY_KEY, "review", "count"], queryFn: () => agentsApi.review({ per_page: 1 }), enabled, ...poll(60_000) })
  return data?.total ?? 0
}

export function useShipped(enabled = true) {
  return useQuery({ queryKey: [CODY_KEY, "shipped"], queryFn: () => agentsApi.shipped({ per_page: 100 }), enabled, ...poll(60_000) })
}

export function useSweeps(enabled = true) {
  return useQuery({ queryKey: [CODY_KEY, "sweeps"], queryFn: () => agentsApi.sweeps({ per_page: 14 }), enabled, ...poll(60_000) })
}

export function useAgentSessions(enabled = true) {
  return useQuery({ queryKey: [CODY_KEY, "sessions"], queryFn: agentsApi.sessions, enabled, ...poll(10_000) })
}

export function useAgentRepos(enabled = true) {
  return useQuery({ queryKey: [CODY_KEY, "repos"], queryFn: agentsApi.repos, enabled })
}

/** A task's runs, newest first (the agent panel on the task page). */
export function useTaskAgentRuns(taskId: number | null | undefined, enabled = true) {
  return useQuery({
    queryKey: [CODY_KEY, "runs", { task_id: taskId }],
    queryFn: () => agentsApi.runs({ task_id: taskId!, per_page: 20 }),
    enabled: enabled && taskId != null,
    ...poll(20_000),
  })
}

/**
 * Open runs keyed by task id, for the bot badge on task rows: one request
 * for the whole list rather than one per row.
 */
export function useOpenRunsByTask(enabled: boolean) {
  const { data } = useQuery({
    queryKey: [CODY_KEY, "runs", { outcome: "open", per_page: 200 }],
    queryFn: () => agentsApi.runs({ outcome: "open", per_page: 200 }),
    enabled,
    staleTime: 30_000,
    ...poll(60_000),
  })
  return openRunsByTask(data?.data ?? [])
}

/**
 * The boards the Cody user belongs to, for the task pickers. Shares the
 * overview's cache; null until known (owners only), which leaves the board
 * check out.
 */
export function useCodyBoards(enabled: boolean): string[] | null {
  const { data } = useQuery({ queryKey: [CODY_KEY, "overview"], queryFn: agentsApi.overview, enabled, staleTime: 5 * 60_000 })
  return data?.agents.find((a) => a.kind === "coder")?.user?.boards ?? null
}

/** Cody's disabled state for the task pickers (owners only ask); null while it can be assigned. */
export function useCodyDisabled(enabled: boolean): { reason: string | null } | null {
  const { data } = useQuery({ queryKey: [CODY_KEY, "overview"], queryFn: agentsApi.overview, enabled, staleTime: 5 * 60_000 })
  const coder = data?.agents.find((a) => a.kind === "coder")
  return coder?.status === "disabled" ? { reason: coder.disabled_reason ?? null } : null
}

type Decision = { kind: "approve"; note?: string | null } | { kind: "reject" | "changes"; note: string }

export function useRunDecision(runId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (d: Decision) =>
      d.kind === "approve" ? agentsApi.approve(runId, d.note) : d.kind === "reject" ? agentsApi.reject(runId, d.note) : agentsApi.requestChanges(runId, d.note),
    // A 502 merge_failed leaves the run waiting with the error on it, so
    // refresh on failure too.
    onSettled: () => queryClient.invalidateQueries({ queryKey: [CODY_KEY] }),
  })
}

export function useUpdateRepo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: RepoPatch }) => agentsApi.updateRepo(id, patch),
    onSettled: () => queryClient.invalidateQueries({ queryKey: [CODY_KEY] }),
  })
}

export function useUpdateAgent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ slug, status, budget }: { slug: string; status?: AgentStatus; budget?: AgentBudget }) =>
      agentsApi.updateAgent(slug, { ...(status ? { status } : {}), ...(budget ? { budget } : {}) }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: [CODY_KEY] }),
  })
}

export function useSecretMutations() {
  const queryClient = useQueryClient()
  const onSettled = () => queryClient.invalidateQueries({ queryKey: [CODY_KEY, "setup"] })
  return {
    put: useMutation({ mutationFn: ({ key, value }: { key: SecretKey; value: string }) => agentsApi.putSecret(key, value), onSettled }),
    remove: useMutation({ mutationFn: (key: SecretKey) => agentsApi.deleteSecret(key), onSettled }),
  }
}

/** Mints the runner's machine key (shown once; ROOT-SETUP.sh asks for it). */
export function useMintRunnerKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: agentsApi.mintRunnerKey,
    onSettled: () => queryClient.invalidateQueries({ queryKey: [CODY_KEY, "setup"] }),
  })
}

/** Start or pause all three agents in one atomic call. */
export function useStartPause() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (action: "start" | "pause") => (action === "start" ? agentsApi.start() : agentsApi.pause()),
    onSettled: () => queryClient.invalidateQueries({ queryKey: [CODY_KEY] }),
  })
}

export function useUpdateSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (patch: SettingsPatch) => agentsApi.updateSettings(patch),
    onSettled: () => queryClient.invalidateQueries({ queryKey: [CODY_KEY] }),
  })
}

/** Re-review, Take over and Resume on one run. */
export function useRunActions(runId: number) {
  const queryClient = useQueryClient()
  const onSettled = () => queryClient.invalidateQueries({ queryKey: [CODY_KEY] })
  return {
    reReview: useMutation({ mutationFn: () => agentsApi.reReview(runId), onSettled }),
    takeover: useMutation({ mutationFn: () => agentsApi.takeover(runId), onSettled }),
    resume: useMutation({ mutationFn: () => agentsApi.resume(runId), onSettled }),
  }
}
